import { SnackbarService } from "./snackbar.service";
import { API } from "./../models/API";
import { environment } from "./../../environments/environment";
import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { catchError } from "rxjs/operators";
import { Subject, throwError } from "rxjs";
import { tap } from "rxjs/operators";
import {
  RetornoAcaoLimparColuna,
  RetornoAtualizacaoStatusPedido,
  RetornoNovosPedidos,
  RetornoPedidosDoDia,
} from "../models/pedido";

export interface RetornoApi<T> {
  status: "success" | "error";
  data: T;
  metadata: any;
}

export interface EventoConfirmacaoPedido {
  Hash: string;
  Status: number | string;
  ArrayHash?: string[];
}

@Injectable({
  providedIn: "root",
})
export class ApiService {
  headers = environment.headers;
  URL_BASE = environment.baseURL;
  token: string;

  estaAtualizandoStatusPedido = false;

  public onConfirmacaoPedido$ = new Subject<EventoConfirmacaoPedido>();

  constructor(
    private https: HttpClient,
    private snackbarService: SnackbarService
  ) {}

  headerMonitor() {
    return { headers: { Auth: `Monitor ${this.token}` } };
  }

  headerBasic() {
    return { headers: this.headers };
  }

  encodeDataToURL(data: any) {
    if (!data) {
      return "";
    }
    return Object.keys(data)
      .map((value) => `${value}=${encodeURIComponent(data[value])}`)
      .join("&");
  }

  setURL(versao: string, endpoint: string, params: any | null = null) {
    const data = this.encodeDataToURL(params);
    return `${this.URL_BASE}api/${versao}/${endpoint}?${data}`;
  }

  pipeErro(erro: string) {
    this.snackbarService.Error(erro);
    return throwError(erro);
  }

  autenticarUsuario(Codigo: string, Email: string, Senha: string) {
    return this.https
      .post<API<any>>(
        this.setURL("v2", "AutenticarUsuarioMonitor"),
        {
          Codigo,
          Email,
          Senha,
        },
        this.headerBasic()
      )
      .pipe(catchError(this.pipeErro));
  }

  trocarSenha(Senha: string, NovaSenha: number | string) {
    return this.https
      .post<API<any>>(
        this.setURL("v2", "TrocarSenha"),
        { Senha, NovaSenha },
        this.headerMonitor()
      )
      .pipe(catchError(this.pipeErro));
  }

  getPedidos(tempoPermanencia) {
    return this.https
      .get<API<any>>(
        this.setURL("v2", "Pedidos", { tempoPermanencia }),
        this.headerMonitor()
      )
      .pipe(catchError(this.pipeErro));
  }

  getPedidosDoDia() {
    return this.https
      .get<API<RetornoPedidosDoDia>>(
        this.setURL("v2", "PedidosDoDia"),
        this.headerMonitor()
      )
      .pipe(catchError(this.pipeErro));
  }

  getNovosPedidos(
    UltimoPedido: number,
    Atualizacao: string,
    TempoLimite: number
  ) {
    return this.https
      .get<API<RetornoNovosPedidos>>(
        this.setURL("v2", "NovosPedidos", {
          UltimoPedido,
          Atualizacao,
          TempoLimite,
        }),
        this.headerMonitor()
      )
      .pipe(catchError(this.pipeErro));
  }

  salvarStatusPedido(
    Hash: string,
    Status: number | string,
    ArrayHash: string[] = null
  ) {
    this.estaAtualizandoStatusPedido = true;
    return this.https
      .put<API<RetornoAtualizacaoStatusPedido>>(
        this.setURL("v2", "AtualizarStatusPedido"),
        { Hash, Status, ArrayHash },
        this.headerMonitor()
      )
      .pipe(
        tap((result: API<any>) => {
          if (result.status === "success" && result.data?.Status === "2") {
            this.onConfirmacaoPedido$.next({
              Hash,
              Status,
              ArrayHash,
            });
          }

          this.estaAtualizandoStatusPedido = false;
        })
      )
      .pipe(catchError(this.pipeErro));
  }

  ocultarPedidos(ArrayHash: string[] = null) {
    return this.https
      .put<API<any>>(
        this.setURL("v2", "OcultarPedidos"),
        { ArrayHash },
        this.headerMonitor()
      )
      .pipe(catchError(this.pipeErro));
  }

  limparPedidosDoGrupo(ArrayHash: string[] = null) {
    return this.https
      .put<API<RetornoAcaoLimparColuna>>(
        this.setURL("v2", "LimparPedidosDoGrupo"),
        { ArrayHash },
        this.headerMonitor()
      )
      .pipe(catchError(this.pipeErro));
  }

  alterarTempoEntrega(TempoEntrega: number) {
    return this.https
      .put<API<any>>(
        this.setURL("v2", "AlterarTempoEntrega"),
        { TempoEntrega },
        this.headerMonitor()
      )
      .pipe(catchError(this.pipeErro));
  }

  alterarStatusAberturaLoja(StatusAbertura: number) {
    return this.https
      .put<API<any>>(
        this.setURL("v2", "AlterarStatusLoja"),
        { StatusAbertura },
        this.headerMonitor()
      )
      .pipe(catchError(this.pipeErro));
  }

  limparMonitor(UltimoPedido: number) {
    return this.https
      .get<API<any>>(
        this.setURL("v2", "LimparMonitor", { UltimoPedido }),
        this.headerMonitor()
      )
      .pipe(catchError(this.pipeErro));
  }

  getImpressaoTesteEscPos(NumeroColunasImpressora: number) {
    return this.https
      .post<API<string>>(
        this.setURL("v2", "GetImpressaoTesteEscPos"),
        {
          NumeroColunasImpressora,
        },
        this.headerBasic()
      )
      .pipe(catchError(this.pipeErro));
  }

  getImpressaoPedidoEscPos(Pedido: any, NumeroColunasImpressora = 42) {
    return this.https
      .post<API<string>>(
        this.setURL("v2", "GetImpressaoPedidoEscPos"),
        {
          Pedido,
          NumeroColunasImpressora,
        },
        this.headerBasic()
      )
      .pipe(catchError(this.pipeErro));
  }

  getImpressaoPedidoEscPosByHash(
    Hash: string | null = null,
    ArrayHash: string[] = null,
    NumeroColunasImpressora = 42
  ) {
    return this.https
      .post<API<string[]>>(
        this.setURL("v2", "GetImpressaoPedidoEscPosByHash"),
        {
          Hash,
          ArrayHash,
          NumeroColunasImpressora,
        },
        this.headerBasic()
      )
      .pipe(catchError(this.pipeErro));
  }
}
